import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { apiErrorMessage } from '@/helpers/api-error-message'
import { uploadFile as uploadFileRequest } from './generated/data-transformation/data-transformation'

export interface UploadFileRequest {
  /** The files of one request. A price sheet sends all its files together, so the API can check the pair. */
  files: File[]
  partnerOrganizationId?: string
  documentType: string
  userOrgType: string
  isHeadless?: boolean
  onProgress?: (percent: number) => void
}

/** The outcome the upload hook reports, success or failure; not the API's response. */
export interface UploadFileOutcome {
  fileNames: string[]
  success: boolean
  error?: string
}

/**
 * Upload files to the data transformation service.
 * The files are uploaded to the backend API which handles:
 * - S3 upload
 * - SNS notification
 * - Partnership validation
 */
export const uploadFile = async (
  request: UploadFileRequest
): Promise<UploadFileOutcome> => {
  try {
    await uploadFileRequest(
      {
        file: request.files,
        // An empty partner is left out, as before.
        partnerOrganizationId: request.partnerOrganizationId || undefined,
        documentType: request.documentType,
        userOrgType: request.userOrgType,
        isHeadless: request.isHeadless
      },
      {
        onUploadProgress: (event) => {
          if (!request.onProgress) return
          const total =
            event.total ??
            request.files.reduce((sum, file) => sum + file.size, 0)
          if (!total) return
          const percent = Math.min(
            100,
            Math.round((event.loaded / total) * 100)
          )
          request.onProgress(percent)
        }
      }
    )

    return {
      fileNames: request.files.map((file) => file.name),
      success: true
    }
  } catch (error) {
    return {
      fileNames: request.files.map((file) => file.name),
      success: false,
      // The API says what was wrong in the ProblemDetails detail, such as a price sheet's missing half.
      // Without one (a 413 from the size limit, a network error) the status says what it can.
      error: apiErrorMessage(error, fallbackMessage(error))
    }
  }
}

const fallbackMessage = (error: unknown): string =>
  (axios.isAxiosError(error) && error.response?.statusText) ||
  (error instanceof Error && error.message) ||
  'Upload failed'

/**
 * React Query mutation hook for uploading files.
 *
 * Usage:
 * ```tsx
 * const { mutateAsync: uploadFile, isPending } = useUploadFile()
 *
 * const result = await uploadFile({
 *   files: [myFile],
 *   partnerOrganizationId: 'abc-123',
 *   documentType: 'SPA',
 *   userOrgType: 'Manufacturer'
 * })
 * ```
 */
export const useUploadFile = () => {
  return useMutation({
    mutationFn: uploadFile
  })
}
